
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import I18n from '@/Mixins/I18n'
import Lockable from '@/Mixins/Lockable'
import RequiresIntegration from '@/Mixins/RequiresIntegration'
import PaymentMethod from '@/Models/Static/PaymentMethod'
import ChangeStrategy from '@/Models/Static/ChangeStrategy'

export default {
  mixins: [
    I18n,
    Lockable,
    RequiresIntegration,
  ],

  props: {
    value: {
      required: true,
      type: String,
    },
  },

  data: () => ({
    PaymentMethod,
    ChangeStrategy,
    zeroDialog: false,
    confirmTipDialog: false,
    askStrategyDialog: false,
    payingMethod: null,
  }),

  computed: {
    ...mapState('workingModes', {
      monkeyMode: 'event',
    }),
    ...mapState('orders', {
      order: 'current',
    }),
    ...mapState('orders/dialogs', {
      dialogsLocked: 'locked',
    }),
    ...mapState('orders/payment', [
      'amountToPay',
      'tipAmount',
    ]),
    ...mapGetters('rummage', [
      'cashPayment',
      'cardPayment',
      'otherPaymentMethods',
      'paymentMethod',
      'hasCashMachine',
    ]),
    ...mapGetters('orders/payment', [
      'willBeChange',
      'mixedAccountablePayments'
    ]),
    showCashPayment () {
      return !! this.cashPayment
    },
    showCardPayment () {
      return !! this.cardPayment
    },
    disableCashPayment () {
      return this.dialogsLocked || this.hasCashMachine
    },
    disableCardPayment () {
      return this.dialogsLocked
        || this.$page.props.standalone
        || this.$page.props.auth.demo
        || this.payingZero
    },
    intAmount () {
      return parseInt(this.value)
    },
    payingAmount () {
      return this.intAmount === 0
        ? (this.amountToPay + this.tipAmount)
        : (this.intAmount / 100)
    },
    payingZero () {
      return this.payingAmount.lessThanOrEqualTo(0)
    },
  },

  methods: {
    ...mapMutations('user', [
      'skipScreenLock',
    ]),
    ...mapActions('alert', [
      'showError',
    ]),
    payBy (method) {
      // Use tryLock to prevent race condition with double clicks
      if (!this.tryLock(2000)) {
        return
      }

      // view FLOWCHART at REV-9146

      if (this.mixedAccountablePayments(method)) {
        this.showError(this.__('notAccountablePaymentsMixError'))
        return
      }

      this.payingMethod = method

      if (this.payingZero && ! this.$store.state.workingModes.event) {
        this.zeroDialog = true
        return
      }

      if (! this.willBeChange(this.payingAmount)) {
        this.pay()
        return
      }

      const changeStrategy = this.paymentMethod(method)?.changeStrategy
      if (changeStrategy == this.ChangeStrategy.DONT_ALLOW) {
        this.showError(this.__('changeNotAllowed'))
        return
      }
      if (changeStrategy == this.ChangeStrategy.WITH_CASH) {
        this.pay()
        return
      }

      if (changeStrategy == this.ChangeStrategy.CONVERT_AS_TIP) {
        this.convertChangeAsTipAndPay()
        return
      }

      if (changeStrategy == this.ChangeStrategy.AS_TIP_WITH_CONFIRMATION) {
        this.confirmTipDialog = true
        return
      }

      this.askStrategyDialog = true
    },
    convertChangeAsTipAndPay() {
      this.pay(true)
    },
    pay (changeAsTip = false) {
      this.zeroDialog = this.confirmTipDialog = this.askStrategyDialog = false
      if (this.payingMethod != this.PaymentMethod.CARD) {
        this.paid(changeAsTip)
        return
      }
      this.skipScreenLock(true)
      Bridge.interfaces.AndroidPayment.pay(
        Number(this.payingAmount * 100).toFixed(),
        JSON.stringify({
          test: !! this.integration?.test,
          config: this.integration?.config ?? {},
        })
      )
        .then(voidReason => {
          this.skipScreenLock(false)
          this.$store.dispatch('alert/snackSuccess', this.__('paymentDone'))
          this.$log(`Paid by card: ${this.payingAmount.toFixed(2)}`, 'Order', this.order.id)
          this.paid(changeAsTip, voidReason)
        })
        .catch(e => {
          this.skipScreenLock(false)
          // Unlock the payment buttons so user can retry
          this.locked = false
          this.$store.commit('orders/dialogs/unlock')
          const err = e?.nativeMessage || 'Payment Interface does not exist'
          this.$log(`Paying by card error: ${err}`, 'Order', this.order.id)
          this.showError(err)
          // Emit event for components that need to know payment failed
          this.$emit('payment-failed', e)
        })
        // the finally method does not work in the current Bridge version
    },
    paid (changeAsTip, voidReason = null) {
      let tipAmount = this.tipAmount
      const amountToPay = Math.max(this.amountToPay + tipAmount, 0)
      let change = this.payingAmount - amountToPay
      if (change.lessThanOrEqualTo(0)) {
        change = 0
      }
      if (changeAsTip) {
        tipAmount += change
        change = 0
      }

      this.$emit('paid', {
        paymentMethod: this.payingMethod,
        payAmount: Math.roundToCurrency(this.payingAmount - change - tipAmount),
        tipAmount: Math.roundToCurrency(tipAmount),
        change: Math.roundToCurrency(change),
        voidReason,
      })
    },
  },
}
